import React, { useEffect, useState } from 'react';
import { Loader2, Pill, Plus, StopCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  medicationsService,
  PatientMedication,
  MedicationSource,
} from '@/services/medicationsService';

interface PatientMedicationListTabProps {
  patientId: string;
}

const SOURCE_LABEL: Record<MedicationSource, string> = {
  patient_reported: 'Patient reported',
  rx_this_clinic: 'Rx from this clinic',
  rx_external: 'Rx from outside',
  imported_ccda: 'Imported (CCDA)',
  pharmacy_fill: 'Pharmacy fill',
};

const PatientMedicationListTab = ({ patientId }: PatientMedicationListTabProps) => {
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const canWrite = isPractitioner || isAdmin;

  const [medications, setMedications] = useState<PatientMedication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [medToStop, setMedToStop] = useState<PatientMedication | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [form, setForm] = useState<{
    display: string;
    code: string;
    dose: string;
    route: string;
    source: MedicationSource;
    note: string;
  }>({
    display: '',
    code: '',
    dose: '',
    route: '',
    source: 'patient_reported',
    note: '',
  });

  const refetch = async () => {
    if (!clinic?.id) return;
    setIsLoading(true);
    try {
      const rows = await medicationsService.getPatientMedications(clinic.id, patientId);
      setMedications(rows);
    } catch (error: any) {
      toast({
        title: 'Failed to load medication list',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, clinic?.id]);

  const resetForm = () => setForm({
    display: '',
    code: '',
    dose: '',
    route: '',
    source: 'patient_reported',
    note: '',
  });

  const handleAdd = async () => {
    if (!clinic?.id || !form.display.trim()) return;
    setIsSaving(true);
    try {
      await medicationsService.createMedication(clinic.id, patientId, {
        medicationDisplay: form.display.trim(),
        medicationCode: form.code.trim() || undefined,
        medicationCodeSystem: form.code.trim()
          ? 'http://www.nlm.nih.gov/research/umls/rxnorm'
          : 'free-text',
        doseText: form.dose.trim() || undefined,
        route: form.route.trim() || undefined,
        status: 'active',
        source: form.source,
        note: form.note.trim() || undefined,
      });
      toast({ title: 'Medication added', variant: 'success' });
      setIsAddOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to add medication',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const requestStop = (med: PatientMedication) => setMedToStop(med);

  const confirmStop = async () => {
    if (!clinic?.id || !medToStop) return;
    setIsStopping(true);
    try {
      await medicationsService.stopMedication(clinic.id, medToStop.id);
      toast({ title: 'Medication marked stopped', variant: 'success' });
      setMedToStop(null);
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to stop',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsStopping(false);
    }
  };

  const active = medications.filter(m =>
    ['active', 'intended', 'on-hold'].includes(m.status),
  );
  const stopped = medications.filter(m => ['completed', 'stopped'].includes(m.status));

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-500">
              <Pill className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Active Medications</h3>
              <p className="text-xs text-slate-500">
                {isLoading ? 'Loading…' : `${active.length} active`}
              </p>
            </div>
          </div>
          {canWrite && (
            <Button size="sm" onClick={() => setIsAddOpen(true)} className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" />
              Add medication
            </Button>
          )}
        </div>

        <div className="p-5 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            </div>
          ) : active.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No active medications recorded</div>
          ) : (
            <div className="space-y-2">
              {active.map(m => (
                <MedicationRow
                  key={m.id}
                  med={m}
                  canWrite={canWrite}
                  onStop={requestStop}
                />
              ))}
            </div>
          )}

          {stopped.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Stopped / completed
              </div>
              <div className="space-y-2">
                {stopped.map(m => (
                  <MedicationRow key={m.id} med={m} canWrite={false} muted />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Sheet open={isAddOpen} onOpenChange={setIsAddOpen}>
        <SheetContent className="w-full !max-w-[480px] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b border-slate-200">
            <SheetTitle>Add Medication</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="display">Medication</Label>
              <Input
                id="display"
                placeholder="e.g., Metformin 500 mg"
                value={form.display}
                onChange={(e) => setForm(prev => ({ ...prev, display: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">RxNorm code (optional)</Label>
              <Input
                id="code"
                placeholder="e.g., 6809"
                value={form.code}
                onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dose">Dose / instructions</Label>
              <Input
                id="dose"
                placeholder="e.g., 500 mg by mouth twice daily"
                value={form.dose}
                onChange={(e) => setForm(prev => ({ ...prev, dose: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="route">Route</Label>
              <Input
                id="route"
                placeholder="oral, subcutaneous, topical…"
                value={form.route}
                onChange={(e) => setForm(prev => ({ ...prev, route: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select
                value={form.source}
                onValueChange={(v: MedicationSource) => setForm(prev => ({ ...prev, source: v }))}
                disabled={isSaving}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient_reported">Patient reported</SelectItem>
                  <SelectItem value="rx_external">Rx from outside</SelectItem>
                  <SelectItem value="imported_ccda">Imported (CCDA)</SelectItem>
                  <SelectItem value="pharmacy_fill">Pharmacy fill</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="note">Notes</Label>
              <textarea
                id="note"
                className="w-full min-h-[80px] rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setIsAddOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!form.display.trim() || isSaving}
              className="rounded-lg"
            >
              {isSaving ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </span>
              ) : 'Add'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!medToStop}
        onOpenChange={(o) => { if (!o && !isStopping) setMedToStop(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='h-5 w-5 text-amber-500' />
              Stop this medication?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {medToStop && (
                <>
                  Mark <span className='font-medium text-slate-900'>
                    {medToStop.medication_display}
                  </span>
                  {medToStop.dose_text ? ` (${medToStop.dose_text})` : ''} as stopped.
                  This moves it out of the active medication list and is recorded
                  in the patient's chart. You can re-add it later if needed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isStopping}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmStop(); }}
              disabled={isStopping}
              className='bg-red-600 hover:bg-red-700 focus:ring-red-500'
            >
              {isStopping ? (
                <span className='flex items-center gap-1.5'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Stopping…
                </span>
              ) : (
                <span className='flex items-center gap-1.5'>
                  <StopCircle className='h-4 w-4' />
                  Stop medication
                </span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const MedicationRow: React.FC<{
  med: PatientMedication;
  canWrite: boolean;
  muted?: boolean;
  onStop?: (med: PatientMedication) => void;
}> = ({ med, canWrite, muted, onStop }) => {
  return (
    <div className={`border border-slate-200 rounded-lg p-3 ${muted ? 'bg-slate-50' : 'bg-white'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
              {med.medication_display}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium capitalize">
              {med.status}
            </span>
            {med.source && (
              <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 text-xs font-medium">
                {SOURCE_LABEL[med.source] || med.source}
              </span>
            )}
          </div>
          {(med.dose_text || med.route) && (
            <div className="mt-1 text-xs text-slate-600">
              {med.dose_text && <span>{med.dose_text}</span>}
              {med.route && <span> · {med.route}</span>}
            </div>
          )}
          {med.last_reconciled_at && (
            <div className="mt-1 text-xs text-slate-500">
              Last reconciled {new Date(med.last_reconciled_at).toLocaleDateString()}
            </div>
          )}
          {med.note && (
            <p className="mt-2 text-xs text-slate-600 leading-relaxed">{med.note}</p>
          )}
        </div>
        {canWrite && onStop && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStop(med)}
            className="h-8 px-2 text-xs flex-shrink-0"
          >
            <StopCircle className="h-3.5 w-3.5 mr-1" />
            Stop
          </Button>
        )}
      </div>
    </div>
  );
};

export default PatientMedicationListTab;
